import { useCallback, useRef, useState } from 'react';
import { WarehouseInventoryProductsService } from '../api/services/WarehouseInventoryProductsService';
import { InventoryFilterState, InventoryItem } from '../types/inventory';
import {
  buildWarehouseInventoryFindAllArgs,
  warehouseQueryPlacementStatus,
} from '../utils/inventory/warehouseInventoryListQuery';
import { mapWarehouseInventoryProductsToInventoryItems } from '../utils/inventory/mapWarehouseInventoryProductToInventoryItem';
import {
  resolveTransferScanSelection,
  transferScanSelectionMessage,
  TransferScanContext,
} from '../utils/inventory/transferScanSelection';

type NotifyFn = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;

export interface UseInventoryTransferScanOptions {
  context: TransferScanContext;
  getFilters: () => InventoryFilterState;
  selectedItems: string[];
  onSelectItem: (item: InventoryItem) => void;
  onNotify: NotifyFn;
  enabled?: boolean;
}

export function useInventoryTransferScan({
  context,
  getFilters,
  selectedItems,
  onSelectItem,
  onNotify,
  enabled = true,
}: UseInventoryTransferScanOptions) {
  const [isScanning, setIsScanning] = useState(false);
  const selectedItemsRef = useRef(selectedItems);

  selectedItemsRef.current = selectedItems;

  const handleScanSubmit = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || !enabled) {
        return;
      }

      setIsScanning(true);
      try {
        const currentFilters = getFilters();
        const scanFilters: InventoryFilterState = {
          ...currentFilters,
          searchTerm: trimmed,
        };

        const isWarehouse = context === 'warehouse';
        const response =
          await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindAll(
            ...buildWarehouseInventoryFindAllArgs({
              page: 1,
              limit: 25,
              filters: scanFilters,
              placementStatus: isWarehouse
                ? warehouseQueryPlacementStatus(currentFilters.status)
                : undefined,
              availabilityStatus:
                !isWarehouse && currentFilters.status && currentFilters.status !== 'ALL'
                  ? currentFilters.status
                  : undefined,
              scope: isWarehouse
                ? {
                    excludeCancelledAvailability: true,
                    warehousePlacementOnly: true,
                  }
                : { assignedPlacementOnly: true },
              includeNumericFilters: false,
            }),
          );

        const items = mapWarehouseInventoryProductsToInventoryItems(
          response.data,
          isWarehouse ? { mapTransferFields: false } : undefined,
        );

        const result = resolveTransferScanSelection(
          trimmed,
          items,
          selectedItemsRef.current,
          context,
        );

        if (result.kind === 'selected') {
          onSelectItem(result.item);
        }

        const { message, severity } = transferScanSelectionMessage(result);
        onNotify(message, severity);
      } catch (error) {
        console.error('Error scanning piece for transfer:', error);
        onNotify('Error al buscar la pieza escaneada', 'error');
      } finally {
        setIsScanning(false);
      }
    },
    [context, enabled, getFilters, onNotify, onSelectItem],
  );

  return { handleScanSubmit, isScanning };
}
